import React from "react";
import styled from "styled-components";
import { FatButton } from "../__Internal__";
import { Box } from "../Box";
import { useEvent } from "../hooks/useEvent";
import { Icon } from "../Icon";
import { PropOption } from "../PropX";
import { style } from "../style";
import { useIsFocused } from "../hooks/useIsFocused";

// GABE TODO: clean this up
const ICON_SIZE = "24px";

type GridTemplateProps = {
  $hasSubtitle: boolean;
  $hasIcon: boolean;
};

const calculateGridTemplate = (props: GridTemplateProps) => {
  const gridTemplateColumns: string[] = [];
  const gridTemplateAreas = {
    row1: [] as string[],
    row2: [] as string[],
    row3: [] as string[],
    row4: [] as string[],
  };

  if (props.$hasIcon) {
    gridTemplateAreas.row1.push("icon");
    gridTemplateAreas.row2.push("icon");
    gridTemplateAreas.row3.push("icon");
    gridTemplateAreas.row4.push("icon");
    gridTemplateColumns.push(ICON_SIZE);
  }

  if (props.$hasSubtitle) {
    gridTemplateAreas.row1.push(".");
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("subtitle");
    gridTemplateAreas.row4.push(".");
    gridTemplateColumns.push("1fr");
  } else {
    gridTemplateAreas.row1.push("title");
    gridTemplateAreas.row2.push("title");
    gridTemplateAreas.row3.push("title");
    gridTemplateAreas.row4.push("title");
    gridTemplateColumns.push("1fr");
  }

  gridTemplateAreas.row1.push("rhs");
  gridTemplateAreas.row2.push("rhs");
  gridTemplateAreas.row3.push("rhs");
  gridTemplateAreas.row4.push("rhs");
  gridTemplateColumns.push("auto");

  return {
    gridTemplateRows: "auto 1fr",
    gridTemplateColumns: gridTemplateColumns.join(" "),
    gridTemplateAreas: `
      '${gridTemplateAreas.row1.join(" ")}'
      '${gridTemplateAreas.row2.join(" ")}'
      '${gridTemplateAreas.row3.join(" ")}'
      '${gridTemplateAreas.row4.join(" ")}'
    `,
    columnGap: "12px",
    rowGap: "0px",
  };
};

const ComboBoxClearContainer = styled.button`
  all: unset;
  cursor: pointer;
  padding: ${style.vars.space[1]};

  &:focus {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -${style.vars.whitespace.xsmall};
  }
`;

type ComboBoxClearProps = {
  onClick(): void;
};

export const ComboBoxClear: React.FC<ComboBoxClearProps> = (props) => {
  const onClick = useEvent<React.MouseEventHandler>((ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    props.onClick();
  });

  return (
    <ComboBoxClearContainer type="button" aria-label="Clear" name="Clear" onClick={onClick}>
      <Icon icon="close" />
    </ComboBoxClearContainer>
  );
};

type Props = {
  name?: string;
  disabled: boolean;
  option: Partial<PropOption>;
  onClick(): void;
  onClear(): void;
};

export const ComboBoxCover: React.FC<Props> = (props) => {
  const { name, disabled, option, onClear } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const hasFocus = useIsFocused(ref);

  const labelledBy = `${name}-label`;

  const gridTemplate = React.useMemo(() => {
    return calculateGridTemplate({
      $hasSubtitle: option.subtitle !== undefined,
      $hasIcon: option.icon !== undefined,
    });
  }, [option.subtitle !== undefined, option.icon !== undefined]);

  const onClick = useEvent<React.MouseEventHandler>((ev) => {
    ev.preventDefault();
    props.onClick();
  });

  const onKeyDown = useEvent<React.KeyboardEventHandler>((ev) => {
    /**
     * Only handle events that are directly on the element.
     */
    if (ev.currentTarget !== ev.target) {
      return;
    }

    if (ev.key === "Escape") {
      ev.preventDefault();
      onClear();
    } else if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      props.onClick();
    }
  });

  return (
    <FatButton.Container
      ref={ref}
      $horizontal={false}
      $highlight={false}
      $disabled={disabled}
      $invalid={false}
      $focused={hasFocus}
      $flatTopLeft={false}
      $flatTopRight={false}
      $flatBottomLeft={false}
      $flatBottomRight={false}
      $compact={true}
      $hideOverflow
      onClick={onClick}
      onKeyDown={onKeyDown}
      role="button"
      tabIndex={0}
    >
      <FatButton.Grid $compact={true} $gridTemplate={gridTemplate}>
        {option.icon && (
          <FatButton.IconWrapper $faded={disabled}>
            <Icon aria-hidden icon={option.icon} xlarge />
          </FatButton.IconWrapper>
        )}

        <FatButton.TitleWrapper $faded={disabled} $hideOverflow id={labelledBy}>
          <FatButton.TitleText>{option.title ?? option.value}</FatButton.TitleText>
        </FatButton.TitleWrapper>

        {option.subtitle && (
          <FatButton.SubtitleWrapper $faded={disabled} $hideOverflow>
            <FatButton.SubtitleText $disabled={disabled} $highlight={false}>
              {option.subtitle}
            </FatButton.SubtitleText>
          </FatButton.SubtitleWrapper>
        )}

        <FatButton.RightSideWrapper $faded={disabled}>
          <Box mr="12px" width="0px" />

          <ComboBoxClear onClick={onClear} />

          <Box mr="5px" width="0px" />
        </FatButton.RightSideWrapper>
      </FatButton.Grid>
    </FatButton.Container>
  );
};
